// shared-lib/cabinet-runs/vendors/venus/resolveSku.js
//
// One emitted cabinet → its Venus SKU, by KIND and size (design 2026-10-05
// §2). Pure; inches throughout. The fill uses the same answer to refuse a
// size Venus does not make, and the estimate to price one — so they cannot
// disagree about what a cabinet is.

const catalog = require('./catalog.json');
const { SINK_FRONTS } = require('./sinkFronts');
const { RACKS, rackOf, rackSkuOf } = require('../../racks');

const IN = 0.0254;
const inches = (m) => Math.round((m / IN) * 2) / 2; // half-inch: 34.5" bodies
const BY_SKU = new Map(catalog.units.map((u) => [u.sku, u]));
const pad2 = (n) => String(Math.round(n)).padStart(2, '0');

// A base this shallow is built from 12"-deep wall cabinets (`resolveSku`).
const SHALLOW_BASE_MAX_DEPTH_IN = 13;
// …the 30" box that, on a 4½" kick, stands at a base's 34½".
const SHALLOW_BASE_BOX_IN = 30;

const ACCESSORY_TYPES = new Set(['blank-panel', 'countertop']);

// The depths Venus makes a vanity cabinet at, read off the price list: every
// VB / VDB / VSB / VSD is 21". A side panel is a panel, not a cabinet.
const VANITY_CABINET_DEPTHS_IN = new Set(
  catalog.units
    .filter((u) => /^vanity-/.test(u.kind) && u.kind !== 'vanity-side-panel')
    .map((u) => u.d)
);

/** The kind of cabinet an emitted object is, under a profile. */
function kindOf(obj, profileId) {
  const c = obj.category || {};
  const d = obj.dimensions || {};
  if (obj.type === 'corner-fill-cabinet') return 'angled-corner';
  if (obj.type === 'corner-cabinet') {
    if (d.cornerVariant === 'single-sided')
      return d.isUpper ? 'wall-blind' : 'corner-blind-base';
    return d.isUpper ? 'wall-pie' : 'lazy-susan';
  }
  if (obj.type === 'upper-cabinet') {
    if (c.blindCorner) return 'wall-blind';
    const deep = obj.label === 'Over-Fridge Cabinet' || inches(d.z) >= 20;
    return deep ? 'wall-bridge-24' : 'wall';
  }
  if (c.tall) return 'tall-pantry';
  // A vanity body is a vanity whatever profile asks: the vanity tools emit it
  // as a plain lower-cabinet labelled 'Vanity Cabinet', and the client flows
  // persist no vanity run, so the estimate asks with no profile at all.
  const vanity = profileId === 'vanity' || obj.label === 'Vanity Cabinet';
  // …and it carries its basins as `sinkCount`, not a sinkBase flag.
  // The island builder flags its sink base 'sink-cabinet'.
  if (c.sinkBase || c['sink-cabinet'] || (vanity && obj.sinkCount > 0)) {
    if (vanity) return 'vanity-sink-base';
    const farm = (obj.columns || []).some(
      (col) => col.type === 'farmhouse-front'
    );
    return farm ? 'farm-sink-base' : 'sink-base';
  }
  // The 2 drawers front (`twoDrawer`) is Venus's DB-2 — a kitchen part only;
  // a vanity asks for a two-drawer base Venus does not make.
  if (c.drawer && c.twoDrawer)
    return vanity ? 'vanity-two-drawer-base' : 'two-drawer-base';
  if (c.drawer) return vanity ? 'vanity-drawer-base' : 'drawer-base';
  return vanity ? 'vanity-base' : 'base';
}

/**
 * Does a box that reads as a plain BASE carry a drawer in its front? The
 * Drawer + Doors front (`applyBayType` 'drawer-doors': `category.drawerDoors`
 * and a `drawer-doors` column). A drawer BANK reads as a drawer base already
 * (`kindOf`), so only this front can put a drawer on a `base`.
 */
function hasDrawerFront(obj) {
  return (
    Boolean(obj.category?.drawerDoors) ||
    (obj.columns || []).some((col) => col?.type === 'drawer-doors')
  );
}

/** The SKU a kind + size would be, before checking Venus makes it. */
function skuFor(kind, w, h, len) {
  switch (kind) {
    case 'base':
      return `B${pad2(w)}`;
    case 'drawer-base':
      return `DB${pad2(w)}`;
    case 'two-drawer-base':
      return `DB${pad2(w)}-2`;
    case 'sink-base':
      return `SB${pad2(w)}`;
    case 'farm-sink-base':
      return `FS${pad2(w)}`;
    case 'wall':
      return `W${pad2(w)}${pad2(h)}`;
    case 'wall-bridge-24':
      return `W${pad2(w)}${pad2(h)}24`;
    case 'wall-blind':
      return `WBC${pad2(w)}${pad2(h)}`;
    case 'wall-pie':
      return `WPC${pad2(w)}${pad2(h)}`;
    case 'tall-pantry':
      return `TP${pad2(w)}${pad2(h)}`;
    case 'vanity-base':
      return `VB${pad2(w)}21`;
    case 'vanity-drawer-base':
      return `VDB${pad2(w)}21`;
    case 'lazy-susan':
      return `LSB${pad2(len)}`;
    case 'corner-blind-base':
      return len <= 42 ? 'BBC39/42' : 'BBC42/45';
    default:
      return null;
  }
}

const HAND = { left: 'L', right: 'R' };

/**
 * A vanity sink of a width with no VSB: Venus's sink-drawer unit. With
 * drawers to one SIDE (`sinkDrawers`, the panel's choice — `sinkUnits.js`)
 * it is that side's handed unit (VSD3621L: drawers on the left), or none
 * where Venus makes no such unit at this width.
 */
function vanitySinkSku(w, bowls = 1, side = null) {
  if (side) {
    const handed = `VSD${pad2(w)}21${HAND[side]}`;
    return bowls !== 2 && BY_SKU.has(handed) ? handed : null;
  }
  if (w === 60) return bowls === 2 ? 'VSD60DOUBLE' : 'VSD60SINGLE';
  if (BY_SKU.has(`VSB${pad2(w)}21`)) return `VSB${pad2(w)}21`;
  if (BY_SKU.has(`VSD${pad2(w)}21`)) return `VSD${pad2(w)}21`;
  return null;
}

/**
 * A fridge column is two of Venus's refrigerator end panels (REP2496, 96" tall,
 * 24" deep), each cut DOWN to the column — so the panel must be at least as
 * tall, and the column no deeper than the panel (user decision 2026-10-08). A
 * surround taller or deeper — one measured from a scan, say — is no Venus
 * part, and says so rather than borrowing the panel's price.
 */
function fridgeSurroundSku(obj) {
  const d = obj.dimensions || {};
  const h = inches(d.y);
  const depth = inches(d.z);
  const panels = catalog.units.filter((u) => u.kind === 'fridge-end-panel');
  const tall = panels.filter((u) => u.h >= h);
  if (!tall.length)
    return { sku: null, reason: `Venus makes no fridge end panel ${h}" tall` };
  const fits = tall.filter((u) => u.d >= depth);
  if (!fits.length)
    return {
      sku: null,
      reason: `Venus makes no fridge end panel ${depth}" deep`,
    };
  const panel = fits.reduce((a, b) => (b.h < a.h ? b : a));
  return { sku: panel.sku, kind: 'fridge-end-panel', count: 2 };
}

const TOL_IN = 0.01;
const unitsOfKind = (kind) => catalog.units.filter((u) => u.kind === kind);
const exactIn = (m) => (m || 0) / IN;
const said = (n) => `${+n.toFixed(2)}"`;

/**
 * The stock a FILLER is ripped from (2026-10-09): Venus sells fillers as 3"
 * and 6" stock — base fillers 34½" long, wall fillers 30/36/42/96" — and the
 * installer cuts one to the gap. So a filler is the narrowest stock that
 * covers its width at the shortest length that covers its height. A gap wider
 * than any stock takes several sticks, and says so (`tooWide`): the drawn run
 * warns, the generator does not build it.
 */
function fillerSku(obj) {
  const d = obj.dimensions || {};
  const w = exactIn(d.x);
  const h = exactIn(d.y);
  const upper =
    obj.type === 'upper-cabinet' || Boolean(obj.category?.['upper-cabinet']);
  const base = unitsOfKind('base-filler');
  const kind =
    !upper && base.some((u) => u.h >= h - TOL_IN)
      ? 'base-filler'
      : 'wall-filler';
  const stock = unitsOfKind(kind).filter((u) => u.h >= h - TOL_IN);
  if (!stock.length)
    return { sku: null, reason: `Venus makes no filler ${said(h)} long` };
  const length = Math.min(...stock.map((u) => u.h));
  const atLength = stock
    .filter((u) => u.h === length)
    .sort((a, b) => a.w - b.w);
  const fits = atLength.find((u) => u.w >= w - TOL_IN);
  if (fits) return { sku: fits.sku, kind };
  const widest = atLength[atLength.length - 1];
  const count = Math.ceil((w - TOL_IN) / widest.w);
  return {
    sku: widest.sku,
    kind,
    count,
    tooWide: true,
    reason: `Venus fillers are ${atLength.map((u) => said(u.w)).join(' or ')} stock; a ${said(w)} gap takes ${count}`,
  };
}

/** The smallest unit of `kind` that covers `need` (by `h`, then `d`, then `w`). */
function coveringUnit(kind, { h = 0, d = 0, w = 0 }) {
  return (
    unitsOfKind(kind)
      .filter(
        (u) => u.h >= h - TOL_IN && u.d >= d - TOL_IN && u.w >= w - TOL_IN
      )
      .sort((a, b) => a.h - b.h || a.d - b.d || a.w - b.w)[0] || null
  );
}

/**
 * A PANEL (`blank-panel`) as the Venus part it is cut from (2026-10-09): the
 * panel at a dishwasher's open end is Venus's dishwasher return (its filler
 * the panel's width), a finished BACK — an island's or a peninsula's, the
 * generator's or the run engine's (`category.backPanel`; `island` on one
 * persisted before the flag) — is back-panel sheet, one per 96" of length,
 * its 48" side cut to the box, and any other is the side panel that covers
 * it. Thinnest sheet that will do.
 */
function panelSku(obj, profileId) {
  const c = obj.category || {};
  const d = obj.dimensions || {};
  const w = exactIn(d.x);
  const h = exactIn(d.y);
  const depth = exactIn(d.z);
  if (c.dwEndPanel) {
    const unit =
      coveringUnit('dishwasher-return', { h, w }) ||
      unitsOfKind('dishwasher-return').sort((a, b) => b.w - a.w)[0];
    return unit
      ? { sku: unit.sku, kind: 'dishwasher-return' }
      : { sku: null, reason: 'Venus makes no dishwasher return' };
  }
  // the header panel over a fridge (`fridgeHeader.js`): cut from the ¾"
  // panel sheet that covers it
  if (c.fridgeHeader) {
    const sheet = unitsOfKind('back-panel')
      .filter(
        (u) =>
          Math.abs(u.d - depth) <= TOL_IN &&
          u.h >= h - TOL_IN &&
          u.w >= w - TOL_IN
      )
      .sort((a, b) => a.h - b.h)[0];
    return sheet
      ? { sku: sheet.sku, kind: 'back-panel' }
      : {
          sku: null,
          reason: `Venus makes no ${said(depth)} panel ${said(w)} x ${said(h)}`,
        };
  }
  if (c.backPanel || c.island) {
    const sheet = unitsOfKind('back-panel')
      .filter((u) => u.h >= h - TOL_IN)
      .sort((a, b) => a.d - b.d || a.h - b.h)[0];
    if (!sheet)
      return { sku: null, reason: `Venus makes no back panel ${said(h)} tall` };
    const count = Math.ceil((w - TOL_IN) / sheet.w);
    return {
      sku: sheet.sku,
      kind: 'back-panel',
      ...(count > 1 ? { count } : {}),
    };
  }
  const kinds =
    profileId === 'vanity'
      ? ['vanity-side-panel', 'base-side-panel']
      : ['base-side-panel'];
  for (const kind of kinds) {
    const unit = coveringUnit(kind, { h, d: depth });
    if (unit) return { sku: unit.sku, kind };
  }
  return {
    sku: null,
    reason: `Venus makes no side panel ${said(depth)} deep, ${said(h)} tall`,
  };
}

/** The drawn style of a glass door. */
const GLASS_FRONT = 'glass-front';

/**
 * The glass door Venus makes for the wall box `boxSku` (2026-10-21, plan
 * C10): a GDW is the DOOR for the W of the same size, not a whole unit — the
 * sheet's W…G combination rows are W + GDW in every colour — so a glass upper
 * is priced as its box plus this door. `{ sku: null, reason }` when Venus
 * makes none for that box (its 39" and 42" widths, the 21" and 24" heights).
 */
function glassDoorFor(boxSku) {
  const door = catalog.units.find(
    (u) => u.kind === 'glass-door' && u.fits === boxSku
  );
  return door
    ? { sku: door.sku }
    : {
        sku: null,
        reason: `Venus Cabinetry makes no glass door for ${boxSku}`,
      };
}

/**
 * The roll-out tray Venus makes for a base `w` wide (2026-10-25): an RD,
 * sized to the base's width — an add-on part, since Venus sells no base with
 * roll-outs built in. `{ sku: null, reason }` for a width it makes none for.
 */
function rollOutTrayFor(w) {
  const tray = catalog.units.find(
    (u) => u.kind === 'rollout-tray' && u.w === w
  );
  return tray
    ? { sku: tray.sku }
    : {
        sku: null,
        reason: `Venus Cabinetry makes no roll-out tray for a ${w}" base`,
      };
}

/**
 * The glass holder Venus makes for a wall cabinet `w` wide (group 5): a GL,
 * its own part under the box, in the box's colour. `{ sku: null, reason }`
 * for a width it makes none for.
 */
function glassHolderFor(w) {
  const holder = catalog.units.find(
    (u) => u.kind === 'glass-holder' && u.w === w
  );
  return holder
    ? { sku: holder.sku }
    : {
        sku: null,
        reason: `Venus Cabinetry makes no glass holder for a ${w}" wall cabinet`,
      };
}

/** Does this box carry roll-out trays (`rollOuts.js`: 1..3)? */
const wantsRollOuts = (obj) =>
  Number.isInteger(obj.rollOuts) && obj.rollOuts > 0;

/**
 * A microwave cabinet (`microwaveCabinets.js`, group 4) as Venus's part: the
 * base BMC30, the wall WM30 at its height.
 */
function microwaveSku(obj) {
  const d = obj.dimensions || {};
  const w = inches(d.x);
  const base = Boolean(obj.category?.microwaveBase);
  const sku = base ? `BMC${pad2(w)}` : `WM${pad2(w)}${pad2(inches(d.y))}`;
  if (BY_SKU.has(sku))
    return { sku, kind: base ? 'base-microwave' : 'wall-microwave' };
  return { sku: null, reason: `Venus makes no microwave cabinet ${sku}` };
}

/**
 * A pull-out base (`pullOuts.js`, group 4) as Venus's part: the trash
 * pull-out B18TR, the spice pull-out BF09-PO — each made at one width.
 */
function pullOutSku(obj) {
  const w = inches(obj.dimensions?.x);
  const trash = Boolean(obj.category?.trashPullOut);
  const sku = trash ? `B${pad2(w)}TR` : `BF${pad2(w)}-PO`;
  if (BY_SKU.has(sku))
    return { sku, kind: trash ? 'trash-base' : 'spice-base' };
  return {
    sku: null,
    reason: `Venus makes no ${trash ? 'trash' : 'spice'} pull-out ${sku}`,
  };
}

/**
 * An end shelf (`endShelves.js`, group 3e) as Venus's part: a base one is
 * BES12L / BES12R by the side it stands open on, a wall one OE12 at its
 * height. `{ sku: null, reason }` where Venus makes none.
 */
function endShelfSku(obj) {
  const d = obj.dimensions || {};
  const w = inches(d.x);
  const upper =
    obj.type === 'upper-cabinet' || Boolean(obj.category?.['upper-cabinet']);
  if (upper) {
    const sku = `OE${pad2(w)}${pad2(inches(d.y))}`;
    return BY_SKU.has(sku)
      ? { sku, kind: 'wall-end-shelf' }
      : { sku: null, reason: `Venus makes no wall end shelf ${sku}` };
  }
  if (!obj.endShelfSide)
    return {
      sku: null,
      reason:
        'This end shelf has cabinets on both sides, so no side stands open',
    };
  const sku = `BES${pad2(w)}${HAND[obj.endShelfSide]}`;
  return BY_SKU.has(sku)
    ? { sku, kind: 'base-end-shelf' }
    : { sku: null, reason: `Venus makes no base end shelf ${sku}` };
}

function resolveSku(obj, { profileId } = {}) {
  const c = obj.category || {};
  if (obj.type === 'fridge-surround') return fridgeSurroundSku(obj);
  if (c.endShelf) return endShelfSku(obj);
  // an angled end (`angledEnds.js`, group 6): BEC24, WAE1230 / 36 / 42
  if (c.angledEnd) {
    const d = obj.dimensions || {};
    const upper = obj.type === 'upper-cabinet' || Boolean(c['upper-cabinet']);
    const sku = upper
      ? `WAE${pad2(inches(d.x))}${pad2(inches(d.y))}`
      : `BEC${pad2(inches(d.x))}`;
    return BY_SKU.has(sku)
      ? { sku, kind: upper ? 'wall-angled-end' : 'base-angled-end' }
      : { sku: null, reason: `Venus makes no angled end ${sku}` };
  }
  if (c.trashPullOut || c.spicePullOut) return pullOutSku(obj);
  if (c.microwaveBase || c.microwaveShelf) return microwaveSku(obj);
  // a rack (`racks.js`, group 5): WR3015, WR1515, PR3015, WC0636
  const rackSku = rackSkuOf(obj);
  if (rackSku)
    return BY_SKU.has(rackSku)
      ? { sku: rackSku, kind: RACKS[rackOf(obj)].kind }
      : {
          sku: null,
          reason: `Venus makes no ${RACKS[rackOf(obj)].name} ${rackSku}`,
        };
  if (c.wallDrawers) {
    const d = obj.dimensions || {};
    const sku = `WD${pad2(inches(d.x))}${pad2(inches(d.y))}`;
    return BY_SKU.has(sku)
      ? { sku, kind: 'wall-drawers' }
      : {
          sku: null,
          reason: `Venus makes no wall cabinet with drawers ${sku}`,
        };
  }
  if (obj.type === 'blank-panel') return panelSku(obj, profileId);
  if (c.filler) return fillerSku(obj);
  if (
    c.openShelf ||
    c.floatingShelves ||
    c.valanceBay ||
    ACCESSORY_TYPES.has(obj.type)
  )
    return {
      sku: null,
      accessory: true,
      reason: 'not sold as a unit (accessory)',
    };
  const kind = kindOf(obj, profileId);
  if (kind === 'angled-corner')
    return { sku: null, reason: 'Venus makes no angled corner base' };
  const d = obj.dimensions || {};
  const w = inches(d.x);
  const h = inches(d.y);
  const len = Math.max(inches(d.alongA || 0), inches(d.alongB || 0), w);
  // A vanity cabinet is a Venus part only at a depth Venus makes one. Built
  // any other depth — the narrow 15.3" carcass, a scan kept as built — it is
  // a cabinet Venus does not sell, and was priced as the 21" part of its
  // width (user ruling 2026-11-02: Venus vanities are 21" only). Unlike a
  // kitchen base there is no shallow stand-in: 12" wall cabinets on a kick
  // are too shallow for a vanity.
  if (/^vanity-/.test(kind) && !VANITY_CABINET_DEPTHS_IN.has(inches(d.z)))
    return {
      sku: null,
      reason: `Venus makes vanity cabinets ${[...VANITY_CABINET_DEPTHS_IN]
        .map((n) => `${n}"`)
        .join(' or ')} deep, not ${said(exactIn(d.z))}`,
    };
  // A 12"-deep base (the generator's shallow run, or a run drawn at 12") is a
  // Venus WALL cabinet, 30" tall, on a 4½" toe-kick base built on site — which
  // lands it at 34½" like every base (2026-10-09). Venus makes no shallow base.
  if (
    (kind === 'base' || kind === 'drawer-base' || kind === 'two-drawer-base') &&
    inches(d.z) <= SHALLOW_BASE_MAX_DEPTH_IN
  ) {
    const wall = catalog.units.find(
      (u) => u.kind === 'wall' && u.w === w && u.h === SHALLOW_BASE_BOX_IN
    );
    return wall
      ? { sku: wall.sku, kind: 'wall', kickBase: true }
      : {
          sku: null,
          reason: `Venus makes no ${w}" wall cabinet to stand on a kick`,
        };
  }
  const side = HAND[obj.sinkDrawers] ? obj.sinkDrawers : null;
  // Drawers beside the basin are a VANITY sink unit (VSD…L / R); Venus sells
  // no kitchen sink base with them.
  if (side && kind !== 'vanity-sink-base')
    return {
      sku: null,
      reason: `Venus makes no ${kind} with drawers on the ${side}`,
    };
  const sku =
    kind === 'vanity-sink-base'
      ? vanitySinkSku(w, obj.sinkCount, side)
      : skuFor(kind, w, h, len);
  if (side && !sku)
    return {
      sku: null,
      reason: `Venus makes no ${w}" vanity sink cabinet with drawers on the ${side}`,
    };
  if (sku && BY_SKU.has(sku))
    return {
      sku,
      kind,
      ...(kind === 'wall' && obj.doorStyle === GLASS_FRONT
        ? { glassDoor: glassDoorFor(sku) }
        : {}),
      // a glass holder under it, its own part (GL), by the box's width
      ...(kind === 'wall' && obj.glassHolder === true
        ? { glassHolder: glassHolderFor(w) }
        : {}),
      // a base's roll-out trays, its own part (RD), by the base's width
      ...(kind === 'base' && wantsRollOuts(obj)
        ? { rollOutTray: rollOutTrayFor(w) }
        : {}),
    };
  // A base width Venus makes only as a full-door unit (9": BF0924, no B09) —
  // a full-height door with NO drawer, so it is the part only for a box with
  // no drawer front. A 9" Drawer + Doors base is not something Venus makes,
  // and pricing it as BF0924 drew a drawer the part does not have.
  if (kind === 'base') {
    const fullDoor = catalog.units.find(
      (u) => u.kind === 'base-full-door' && u.w === w
    );
    if (fullDoor && !hasDrawerFront(obj)) {
      return { sku: fullDoor.sku, kind: 'base-full-door' };
    }
    if (fullDoor) {
      return {
        sku: null,
        fullDoorOnly: true,
        reason: `Venus makes ${w}" bases only as a full-height door, with no drawer`,
      };
    }
  }
  const size =
    kind.startsWith('wall') || kind === 'tall-pantry'
      ? `${w}" × ${h}"`
      : `${w}"`;
  return { sku: null, reason: `Venus makes no ${kind} ${size}` };
}

module.exports = { catalog, resolveSku, kindOf, sinkFronts: SINK_FRONTS };
